import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDOP, formatDate } from "@/lib/format";
import { Plus, Trash2, ArrowRight, Eye, AlertTriangle, Clock, History } from "lucide-react";
import PrintMenu from "@/components/PrintMenu";
import DocSearchBar from "@/components/DocSearchBar";
import ProductPicker from "@/components/ProductPicker";
import DeliveryNoteBuilder from "@/components/DeliveryNoteBuilder";
import { useConfirm } from "@/components/ConfirmDialog";
import CustomerCombobox from "@/components/CustomerCombobox";
import { customerPayload } from "@/lib/occasional";

export function DocBuilder({ open, onOpenChange, kind, onSubmit, initialInvoiceId = "" }) {
  // kind: "quotation" (histórico; delivery_note se maneja con DeliveryNoteBuilder)
  const [items, setItems] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [validDays, setValidDays] = useState(14);
  const [maxValidDays, setMaxValidDays] = useState(30);

  useEffect(() => {
    if (open) {
      api.get("/settings/quotation").then(({ data }) => {
        setValidDays(data?.default_valid_days || 14);
        setMaxValidDays(data?.max_valid_days || 30);
      }).catch(() => {});
      setItems([]); setCustomer(null);
    }
    // eslint-disable-next-line
  }, [open, kind, initialInvoiceId]);

  const addProduct = (p) => {
    setItems((it) => {
      const exist = it.find((x) => x.product_id === p.id);
      if (exist) return it.map((x) => x.product_id === p.id ? { ...x, quantity: x.quantity + 1 } : x);
      return [...it, { product_id: p.id, sku: p.sku, name: p.name, quantity: 1, price: p.price, discount: 0, itbis_applies: p.itbis_applies }];
    });
  };

  const totals = useMemo(() => {
    let subtotal = 0, itbis = 0;
    items.forEach((it) => {
      const line = it.quantity * it.price - it.discount;
      subtotal += line;
      if (it.itbis_applies) itbis += line * 0.18;
    });
    return { subtotal, itbis, total: subtotal + itbis };
  }, [items]);

  const submit = () => {
    if (items.length === 0) return toast.error("Agrega al menos un producto");
    onSubmit({
      ...customerPayload(customer),
      items: items.map((it) => ({ ...it, quantity: Number(it.quantity), price: Number(it.price), discount: Number(it.discount || 0) })),
      valid_days: Number(validDays),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nueva cotización</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="overline">Cliente</Label>
            <div className="mt-1">
              <CustomerCombobox value={customer} onChange={setCustomer} allowOccasional
                placeholder="Buscar cliente…" testid="quotation-customer-select" />
            </div>
          </div>
          <div>
            <Label className="overline">Validez (días)</Label>
            <Input
              type="number"
              min={1}
              max={maxValidDays}
              value={validDays}
              onChange={(e) => setValidDays(e.target.value)}
              className="mt-1"
              data-testid="quotation-valid-days-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">Máximo permitido: {maxValidDays} días.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-2">
          <div>
            <Label className="overline">Buscar producto</Label>
            <div className="mt-1">
              <ProductPicker
                onSelect={addProduct}
                testidPrefix="quotation-picker"
                placeholder="SKU, nombre o código de barras (Enter agrega si hay una sola coincidencia)"
              />
            </div>
          </div>
          <div>
            <Label className="overline">Items</Label>
            <div className="border border-border rounded-md mt-1 max-h-[260px] overflow-y-auto">
              {items.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">Sin items</div>}
              {items.map((it, idx) => (
                <div key={idx} className="p-2 border-b border-border/60 text-sm">
                  <div className="flex justify-between gap-2">
                    <div className="truncate font-semibold">{it.name}</div>
                    <button onClick={() => setItems((arr) => arr.filter((_, i) => i !== idx))}><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <Input type="number" step="1" inputMode="decimal" value={it.quantity} onChange={(e) => setItems((arr) => arr.map((x, i) => i === idx ? { ...x, quantity: Number(e.target.value) } : x))} className="h-8 font-mono" />
                    <Input type="number" step="0.01" value={it.price} onChange={(e) => setItems((arr) => arr.map((x, i) => i === idx ? { ...x, price: Number(e.target.value) } : x))} className="h-8 font-mono" />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-2 text-sm space-y-0.5 text-right">
              <div>Subtotal: <span className="data-cell">{formatDOP(totals.subtotal)}</span></div>
              <div>ITBIS: <span className="data-cell">{formatDOP(totals.itbis)}</span></div>
              <div className="font-bold">Total: <span className="data-cell text-primary">{formatDOP(totals.total)}</span></div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} className="bg-primary font-bold" data-testid="save-doc-btn">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Quotations() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [criteria, setCriteria] = useState({ number: true, customer: true });
  const [marginDialog, setMarginDialog] = useState(null); // { quotation, issues }
  const [forceLogOpen, setForceLogOpen] = useState(false);
  const confirm = useConfirm();

  const load = async () => setItems((await api.get("/quotations")).data);
  useEffect(() => { load(); }, []);

  const submit = async (payload) => {
    try { await api.post("/quotations", payload); toast.success("Cotización creada"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const convert = async (q, { force = false, reason = "" } = {}) => {
    if (!force) {
      const ok = await confirm({
        title: `Convertir cotización ${q.number}`,
        description: `Se generará una factura a partir de la cotización de ${q.customer_name}. ¿Continuar?`,
        confirmLabel: "Convertir",
      });
      if (!ok) return;
    }
    try {
      const occType = (q.customer_rnc_cedula || "").replace(/\D/g, "").length === 9 ? "E31" : "E32";
      const params = new URLSearchParams({ ncf_type: q.occasional_buyer ? occType : "E32" });
      if (force) params.set("force", "true");
      if (force && reason) params.set("force_reason", reason);
      const { data } = await api.post(`/quotations/${q.id}/convert-to-invoice?${params.toString()}`);
      toast.success(`Factura ${data.number} generada`);
      setMarginDialog(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "MARGIN_DROP") {
        setMarginDialog({ quotation: q, issues: detail.items || [], message: detail.message });
        return;
      }
      toast.error(formatApiError(err));
    }
  };

  const isExpired = (q) => {
    if (q.status !== "vigente" && q.status !== "vencida") return false;
    if (q.is_expired) return true;
    if (!q.valid_until) return false;
    try { return new Date(q.valid_until) < new Date(); } catch { return false; }
  };

  const statusBadge = (q) => {
    const expired = isExpired(q);
    const status = expired && q.status === "vigente" ? "vencida" : q.status;
    const cls = status === "convertida" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
              : status === "vencida"    ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"
              : status === "cancelada"  ? "bg-muted text-muted-foreground"
              :                            "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
    const validUntilTxt = q.valid_until ? ` · Vence ${formatDate(q.valid_until)}` : "";
    return (
      <span
        className={`px-2 py-0.5 rounded font-semibold inline-flex items-center gap-1 ${cls}`}
        title={`Estado: ${status}${validUntilTxt}`}
        data-testid={`quotation-status-${q.number}`}
      >
        {status === "vencida" && <Clock className="w-3 h-3" />}
        {status}
      </span>
    );
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((q) => {
      if (criteria.number && (q.number || "").toLowerCase().includes(s)) return true;
      if (criteria.customer && (q.customer_name || "").toLowerCase().includes(s)) return true;
      return false;
    });
  }, [items, search, criteria]);

  return (
    <PageContainer>
      <PageHeader
        title="Cotizaciones"
        subtitle="Genera presupuestos previos a la venta. Conviértelos en factura con un clic."
        testid="quotations-header"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setForceLogOpen(true)} data-testid="open-force-log-btn">
              <History className="w-4 h-4 mr-1.5" /> Historial de forzados
            </Button>
            <Button onClick={() => setOpen(true)} className="bg-primary font-bold" data-testid="new-quotation-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Nueva
            </Button>
          </div>
        }
      />
      <DocBuilder kind="quotation" open={open} onOpenChange={setOpen} onSubmit={submit} />

      <ForceConversionLogDialog open={forceLogOpen} onOpenChange={setForceLogOpen} />

      <MarginDropDialog
        state={marginDialog}
        onClose={() => setMarginDialog(null)}
        onForce={(reason) => marginDialog && convert(marginDialog.quotation, { force: true, reason })}
      />

      <DocSearchBar
        value={search}
        onChange={setSearch}
        criteria={criteria}
        onCriteriaChange={setCriteria}
        options={[
          { key: "number", label: "Número" },
          { key: "customer", label: "Cliente" },
        ]}
        placeholder="Buscar cotización por número o cliente…"
        testidPrefix="quotations-search"
        totalLabel={`${filtered.length} de ${items.length}`}
      />
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">Número</th><th className="text-left p-3">Cliente</th>
              <th className="text-left p-3">Fecha</th>
              <th className="text-left p-3">Vence</th>
              <th className="text-right p-3">Total</th>
              <th className="text-center p-3">Estado</th><th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((q) => {
              const expired = isExpired(q);
              return (
                <tr key={q.id} className="border-b border-border/60 hover:bg-accent">
                  <td className="p-3 data-cell font-bold">{q.number}</td>
                  <td className="p-3">{q.customer_name}{q.occasional_buyer && <span className="ml-1.5 text-[9px] font-bold px-1 py-0.5 rounded bg-amber-500/15 text-amber-700 uppercase">Ocasional</span>}</td>
                  <td className="p-3 text-xs text-muted-foreground">{formatDate(q.created_at)}</td>
                  <td className={`p-3 text-xs ${expired ? "text-red-600 dark:text-red-400 font-semibold" : "text-muted-foreground"}`}>
                    {q.valid_until ? formatDate(q.valid_until) : "—"}
                  </td>
                  <td className="p-3 text-right data-cell font-bold">{formatDOP(q.total)}</td>
                  <td className="p-3 text-center text-xs">{statusBadge(q)}</td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <PrintMenu
                        url={`/quotations/${q.id}/pdf`}
                        filename={q.number}
                        label=""
                        iconOnly
                        variant="ghost"
                        size="icon"
                        testid={`quotation-pdf-${q.number}`}
                      />
                      {q.status === "vigente" && !expired && (
                        <Button size="sm" variant="outline" onClick={() => convert(q)} className="text-xs" data-testid={`convert-${q.number}`}>
                          <ArrowRight className="w-3 h-3 mr-1" /> Convertir a factura
                        </Button>
                      )}
                      {expired && q.status === "vigente" && (
                        <span
                          className="text-[11px] text-red-600 dark:text-red-400 font-semibold inline-flex items-center gap-1"
                          title="La cotización ya venció. Crea una nueva para poder convertirla en factura."
                          data-testid={`expired-badge-${q.number}`}
                        >
                          <AlertTriangle className="w-3 h-3" /> Vencida
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Sin cotizaciones</td></tr>}
          </tbody>
        </table>
      </div>
    </PageContainer>
  );
}

function MarginDropDialog({ state, onClose, onForce }) {
  const open = !!state;
  const issues = state?.issues || [];
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const submit = () => {
    const r = reason.trim();
    if (r.length < 5) {
      toast.error("Escribe una justificación de al menos 5 caracteres.");
      return;
    }
    onForce(r);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="margin-drop-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertTriangle className="w-5 h-5" /> Margen reducido en items cotizados
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {state?.message || "El margen de los siguientes items bajó a la mitad o menos desde que se cotizó."}
        </p>
        <div className="border border-border rounded-md overflow-x-auto mt-2">
          <table className="w-full text-xs">
            <thead className="overline border-b border-border bg-muted/40">
              <tr>
                <th className="text-left p-2">SKU</th>
                <th className="text-left p-2">Producto</th>
                <th className="text-right p-2">Precio</th>
                <th className="text-right p-2">Costo cotizado</th>
                <th className="text-right p-2">Costo actual</th>
                <th className="text-right p-2">Margen original</th>
                <th className="text-right p-2">Margen actual</th>
                <th className="text-right p-2">Caída %</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((it, i) => (
                <tr key={i} className="border-b border-border/60">
                  <td className="p-2 data-cell">{it.sku}</td>
                  <td className="p-2">{it.name}</td>
                  <td className="p-2 text-right data-cell">{formatDOP(it.price)}</td>
                  <td className="p-2 text-right data-cell">{formatDOP(it.cost_at_quote)}</td>
                  <td className="p-2 text-right data-cell font-semibold">{formatDOP(it.cost_now)}</td>
                  <td className="p-2 text-right data-cell">{formatDOP(it.margin_old)}</td>
                  <td className="p-2 text-right data-cell text-red-600 dark:text-red-400 font-semibold">{formatDOP(it.margin_new)}</td>
                  <td className="p-2 text-right data-cell text-red-600 dark:text-red-400 font-semibold">{it.margin_drop_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3">
          <Label className="overline">Justificación (obligatoria si fuerzas)</Label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded-md border border-border bg-background p-2 text-sm"
            placeholder="Ej.: Autorizado por gerencia — cliente antiguo, se acepta pérdida de margen"
            data-testid="margin-reason-input"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Este texto quedará registrado en el historial de conversiones forzadas junto a tu usuario y la fecha.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="margin-cancel-btn">Cancelar</Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={reason.trim().length < 5}
            data-testid="margin-force-btn"
          >
            Convertir de todas formas
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ForceConversionLogDialog({ open, onOpenChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/quotations-force-log")
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="force-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" /> Conversiones forzadas con margen caído
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Auditoría de cotizaciones convertidas en factura a pesar de que el margen cayó a la mitad o menos desde la cotización original.
        </p>
        {loading && <div className="p-6 text-center text-muted-foreground text-sm">Cargando…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-6 text-center text-muted-foreground text-sm" data-testid="force-log-empty">
            Aún no hay conversiones forzadas registradas.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="border border-border rounded-md overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="overline border-b border-border bg-muted/40">
                <tr>
                  <th className="text-left p-2">Fecha</th>
                  <th className="text-left p-2">Cotización</th>
                  <th className="text-left p-2">Factura</th>
                  <th className="text-left p-2">Cliente</th>
                  <th className="text-left p-2">Usuario</th>
                  <th className="text-right p-2">Total</th>
                  <th className="text-right p-2">Ítems afectados</th>
                  <th className="text-left p-2">Justificación</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 align-top" data-testid={`force-log-row-${r.quotation_number}`}>
                    <td className="p-2 text-muted-foreground whitespace-nowrap">{formatDate(r.created_at)}</td>
                    <td className="p-2 data-cell font-bold">{r.quotation_number}</td>
                    <td className="p-2 data-cell">{r.invoice_number}</td>
                    <td className="p-2">{r.customer_name || "—"}</td>
                    <td className="p-2">{r.user_email || "—"}</td>
                    <td className="p-2 text-right data-cell font-semibold">{formatDOP(r.total || 0)}</td>
                    <td className="p-2 text-right">
                      <span className="px-2 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 font-semibold">
                        {(r.items || []).length}
                      </span>
                    </td>
                    <td className="p-2 whitespace-pre-line max-w-[260px]">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="force-log-close">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeliveryNotes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [criteria, setCriteria] = useState({ number: true, invoice: true, customer: true, driver: true });

  const load = async () => setItems((await api.get("/delivery-notes")).data);
  useEffect(() => { load(); }, []);

  const submit = async (payload) => {
    try { await api.post("/delivery-notes", payload); toast.success("Conduce creado"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiError(err)); throw err; }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/delivery-notes/${id}/status?status=${status}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((d) => {
      if (criteria.number && (d.number || "").toLowerCase().includes(s)) return true;
      if (criteria.invoice && (d.invoice_number || "").toLowerCase().includes(s)) return true;
      if (criteria.customer && (d.customer_name || "").toLowerCase().includes(s)) return true;
      if (criteria.driver && (d.driver || "").toLowerCase().includes(s)) return true;
      return false;
    });
  }, [items, search, criteria]);

  return (
    <PageContainer>
      <PageHeader
        title="Conduces"
        subtitle="Despacho de mercancía hacia el cliente. Vincula con factura y registra chofer/vehículo."
        testid="conduces-header"
        actions={<Button onClick={() => setOpen(true)} className="bg-primary font-bold" data-testid="new-conduce-btn"><Plus className="w-4 h-4 mr-1.5" /> Nuevo</Button>}
      />
      <DeliveryNoteBuilder open={open} onOpenChange={setOpen} onSubmit={submit} />
      <DocSearchBar
        value={search}
        onChange={setSearch}
        criteria={criteria}
        onCriteriaChange={setCriteria}
        options={[
          { key: "number", label: "Número" },
          { key: "invoice", label: "Factura" },
          { key: "customer", label: "Cliente" },
          { key: "driver", label: "Chofer" },
        ]}
        placeholder="Buscar conduce por número, factura, cliente o chofer…"
        testidPrefix="conduces-search"
        totalLabel={`${filtered.length} de ${items.length}`}
      />
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">Número</th><th className="text-left p-3">Factura</th>
              <th className="text-left p-3">Cliente</th><th className="text-left p-3">Chofer</th>
              <th className="text-left p-3">Fecha</th>
              <th className="text-center p-3">Estado</th><th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => (
              <tr key={d.id} className="border-b border-border/60 hover:bg-accent">
                <td className="p-3 data-cell font-bold">{d.number}</td>
                <td className="p-3 data-cell text-xs">{d.invoice_number || "—"}</td>
                <td className="p-3">{d.customer_name}</td>
                <td className="p-3 text-xs">{d.driver || "—"}</td>
                <td className="p-3 text-xs text-muted-foreground">{formatDate(d.created_at)}</td>
                <td className="p-3 text-center text-xs"><span className="px-2 py-0.5 bg-muted rounded">{d.status}</span></td>
                <td className="p-3 text-right space-x-1">
                  <PrintMenu
                    url={`/delivery-notes/${d.id}/pdf`}
                    filename={d.number}
                    label=""
                    iconOnly
                    variant="ghost"
                    size="icon"
                    testid={`conduce-pdf-${d.number}`}
                  />
                  {d.status === "pendiente" && <Button size="sm" variant="outline" onClick={() => setStatus(d.id, "despachado")} className="text-xs">Despachar</Button>}
                  {d.status === "despachado" && <Button size="sm" variant="outline" onClick={() => setStatus(d.id, "entregado")} className="text-xs">Entregado</Button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Sin conduces</td></tr>}
          </tbody>
        </table>
      </div>
    </PageContainer>
  );
}
